import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2 } from "lucide-react";
import { useDispatch } from "react-redux";
import useTitanApp from "@/hooks/useTitanApp";
import { createPlan as createPlanAction } from "@/features/travelHealthSlice/travelHealthSlice";
import type { Tier1AssessmentData } from "./TravelHealthTier1Assessment";
import type { TripType } from "@/lib/travelHealth/types/travelHealth.types";
import {
  PGD_IDS,
  PRODUCT_META,
  type PgdId,
} from "@/lib/travelHealth/pgds/pgdIds";
import { evaluateCoAdminMatrix } from "@/lib/travelHealth/coAdmin/coAdminEngine";
import { type CoAdminRuleType } from "@/lib/travelHealth/coAdmin/coAdminRules";
import { findRuleType } from "@/lib/travelHealth/coAdmin/findRuleType";
import type {
  CoAdminConstraint,
  SafetyFlagAck,
  SafetyFlagKey,
  TravelHealthPlanProduct,
} from "@/lib/travelHealth/types/travelHealth.types";
import { generateId } from "@/lib/pharmacyFirst/utils/idGenerator";
import { getTier2Config } from "@/lib/travelHealth/tier2/tier2ConfigRegistry";
import "@/lib/travelHealth/tier2/index";
import type { ScheduleOption } from "@/lib/travelHealth/tier2/tier2Config.types";
import {
  projectTravelHealthSchedule,
  getQualifiedPharmacists,
} from "@/lib/travelHealth/scheduling";
import type {
  AppointmentSlot,
  ScheduleFeasibility,
  SchedulingMember,
} from "@/lib/travelHealth/scheduling";
import { useLocationsSettingsData } from "@/lib/settings/locationsData";
import { initialMembers } from "@/components/Calendar/membersData";
import { AppointmentSlotPicker } from "@/components/Calendar/AppointmentSlotPicker";

// ─────────────────────────────────────────────────────────────────────────────
// LOCAL TYPES — data capture and basket assembly only, no clinical logic
// ─────────────────────────────────────────────────────────────────────────────

interface BasketItem {
  pgdId: string;
  displayName: string;
  isOffLabel: boolean;
  blackTriangle: boolean;
}

type DengueGateState =
  | "idle" // not yet attempted
  | "confirming" // inline panel visible
  | "confirmed" // seropositivity confirmed, added to basket
  | "declined"; // pharmacist declined — stays unselected

type AvailabilityMap = Record<
  string,
  { enabled: boolean; timeBlocks: Array<{ from: string; to: string }> }
>;

interface FlatGate {
  key: string;
  text: string;
  productNames: string[];
  confirmed: boolean;
}

interface FlatExclusion {
  key: string;
  question: string;
  products: Array<{
    pgdId: string;
    productName: string;
    checkId: string;
    disqualifyingAnswer: "yes" | "no";
    disqualificationMessage: string;
    referralMessage?: string;
  }>;
  answer: "yes" | "no" | undefined;
  autoAnswered: boolean;
  autoAnswerSource?: string;
  explainerText?: string;
}

interface FlatCaution {
  key: string;
  question: string;
  products: Array<{
    pgdId: string;
    productName: string;
    checkId: string;
    cautionMessage: string;
  }>;
  answer: "yes" | "no" | undefined;
  autoAnswered: boolean;
  autoAnswerSource?: string;
}

type AppointmentSlotWithTime = AppointmentSlot & {
  selectedTime?: string;
  selectedPharmacistName?: string;
  selectedPharmacistId?: string;
  selectedServiceName?: string;
  selectedServiceId?: string;
};

interface IntervalViolation {
  pgdId: string;
  productName: string;
  doseNumber: number;
  doseLabel: string;
  requiredDate: string; // earliest valid date per intervalFromDose1Days
  actualDate: string;
}

function checkIntervalViolations(
  slots: AppointmentSlotWithTime[],
  scheduledProducts: {
    pgdId: string;
    doses: { doseNumber: number; intervalFromDose1Days?: number }[];
  }[],
): IntervalViolation[] {
  const violations: IntervalViolation[] = [];
  // Flatten all doses with their actual scheduled dates
  const allDoses = slots.flatMap((slot) =>
    slot.doses.map((d) => ({ ...d, actualDate: slot.date })),
  );
  for (const product of scheduledProducts) {
    const dose1 = allDoses.find(
      (d) => d.pgdId === product.pgdId && d.doseNumber === 1,
    );
    if (!dose1) continue;
    for (const doseConfig of product.doses) {
      if (
        doseConfig.doseNumber === 1 ||
        doseConfig.intervalFromDose1Days === undefined
      ) {
        continue;
      }
      const actualDose = allDoses.find(
        (d) => d.pgdId === product.pgdId && d.doseNumber === doseConfig.doseNumber,
      );
      if (!actualDose) continue;
      const dose1Date = new Date(`${dose1.actualDate}T00:00:00`);
      const requiredDate = new Date(dose1Date);
      requiredDate.setDate(
        requiredDate.getDate() + doseConfig.intervalFromDose1Days,
      );
      const actualDateObj = new Date(`${actualDose.actualDate}T00:00:00`);
      if (actualDateObj.getTime() < requiredDate.getTime()) {
        violations.push({
          pgdId: product.pgdId,
          productName: actualDose.productName,
          doseNumber: doseConfig.doseNumber,
          doseLabel: actualDose.doseLabel,
          requiredDate: requiredDate.toISOString().split("T")[0],
          actualDate: actualDose.actualDate,
        });
      }
    }
  }
  return violations;
}

export interface Tier1SelectionSavedState {
  step: number;
  nathnacConsulted: boolean;
  nathnacDestinationsAssessed: string;
  nathnacVaccinesRecommended: string;
  nathnacMalariaRiskResistanceAgent: string;
  nathnacEntryRequirements: string;
  nathnacOtherMedicinesIndicated: string;
  acknowledgedFlags: Record<string, any>;
  basket: BasketItem[];
  dengueGateState: string;
  dengueSeropositiveConfirmed: boolean;
  dengueEvidenceDescription: string;
  menACWYEntitlementAcknowledged: boolean | undefined;
  selectedSchedules: Record<string, string>;
  coAdminAckChecked: boolean;
  prescreenCautionAcknowledged: boolean;
  prescreenComplete: boolean;
}

interface TravelHealthTier1SelectionProps {
  tier1Data: Tier1AssessmentData; // from TravelHealthTier1Assessment
  patientId: string;
  patientAge: number;
  patientSex: string;
  patientDob: string; // ISO date string e.g. "1990-04-15"
  siteId: string;
  isYellowFeverCentre: boolean; // from site settings — passed by parent
  yellowFeverCentreNumber: string; // from site settings — passed by parent
  restoredState?: Tier1SelectionSavedState;
  onAutoSaveSnapshot?: (snapshot: Tier1SelectionSavedState) => void;
  onComplete: (
    planId: string,
    appointmentSlots: AppointmentSlot[],
    supplyTaskDates: Record<string, string>,
  ) => void; // called with the new plan id
  onCancel: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// PRODUCT METADATA — local lookups not carried on ProductMeta
// ─────────────────────────────────────────────────────────────────────────────

const OFF_LABEL_PGDS: Set<PgdId> = new Set([
  PGD_IDS.DOXYCYCLINE,
  PGD_IDS.ACETAZOLAMIDE,
]);

const BLACK_TRIANGLE_PGDS: Set<PgdId> = new Set([
  PGD_IDS.CHIKUNGUNYA,
  PGD_IDS.DENGUE,
]);

const FEMALE_ONLY_PGDS: Set<PgdId> = new Set([
  PGD_IDS.NORETHISTERONE,
]);

const ANTIMALARIAL_PGDS: Set<string> = new Set([
  PGD_IDS.MALARONE,
  PGD_IDS.MEFLOQUINE,
  PGD_IDS.DOXYCYCLINE,
]);

interface AntimalarialBrandTiming {
  daysBefore: number; // days before departure to start
  daysAfter: number; // days to continue after leaving risk area
  isWeekly: boolean; // true for mefloquine
  // For daily regimens: tablets per administration
  dailyTablets?: number;
  // For weekly regimens: fraction of tablet per administration
  weeklyFraction?: number;
  unit: "tablet" | "capsule" | "paediatric tablet";
}

const ANTIMALARIAL_BRAND_TIMING: Record<string, AntimalarialBrandTiming> = {
  // Malarone — start 2 days before, 7 days after (use 2 for calculation)
  "malarone-band-11-20": {
    daysBefore: 2,
    daysAfter: 7,
    isWeekly: false,
    dailyTablets: 1,
    unit: "paediatric tablet",
  },
  "malarone-band-21-30": {
    daysBefore: 2,
    daysAfter: 7,
    isWeekly: false,
    dailyTablets: 2,
    unit: "paediatric tablet",
  },
  "malarone-band-31-40": {
    daysBefore: 2,
    daysAfter: 7,
    isWeekly: false,
    dailyTablets: 3,
    unit: "paediatric tablet",
  },
  "malarone-band-over-40": {
    daysBefore: 2,
    daysAfter: 7,
    isWeekly: false,
    dailyTablets: 1,
    unit: "tablet",
  },
  // Mefloquine — start 10 days before departure (matches mefloquine.config.ts
  // tolerability-assessment schedule), 28 days after (4 weeks)
  "mefloquine-band-5-19": {
    daysBefore: 10,
    daysAfter: 28,
    isWeekly: true,
    weeklyFraction: 0.25,
    unit: "tablet",
  },
  "mefloquine-band-20-30": {
    daysBefore: 10,
    daysAfter: 28,
    isWeekly: true,
    weeklyFraction: 0.5,
    unit: "tablet",
  },
  "mefloquine-band-31-45": {
    daysBefore: 10,
    daysAfter: 28,
    isWeekly: true,
    weeklyFraction: 0.75,
    unit: "tablet",
  },
  "mefloquine-band-over-45": {
    daysBefore: 10,
    daysAfter: 28,
    isWeekly: true,
    weeklyFraction: 1,
    unit: "tablet",
  },
  // Doxycycline — start 2 days before, 28 days after
  "doxycycline-100mg": {
    daysBefore: 2,
    daysAfter: 28,
    isWeekly: false,
    dailyTablets: 1,
    unit: "capsule",
  },
};

const GROUP_1_PGD_IDS: PgdId[] = [
  PGD_IDS.HEP_A,
  PGD_IDS.HEP_B,
  PGD_IDS.HEP_AB,
  PGD_IDS.TYPHOID,
  PGD_IDS.CHOLERA,
  PGD_IDS.MENACWY,
  PGD_IDS.DTP_REVAXIS,
  PGD_IDS.JAPANESE_ENC,
  PGD_IDS.RABIES,
  PGD_IDS.TBE,
  PGD_IDS.CHIKUNGUNYA,
  PGD_IDS.DENGUE,
  PGD_IDS.YELLOW_FEVER,
];

const GROUP_2_PGD_IDS: PgdId[] = [
  PGD_IDS.MALARONE,
  PGD_IDS.MEFLOQUINE,
  PGD_IDS.DOXYCYCLINE,
];

const GROUP_3_PGD_IDS: PgdId[] = [
  PGD_IDS.ACETAZOLAMIDE,
  PGD_IDS.MELATONIN,
  PGD_IDS.NORETHISTERONE,
  PGD_IDS.RIFAXIMIN,
];

const PRODUCT_GROUPS: { label: string; pgdIds: PgdId[] }[] = [
  { label: "Travel vaccines", pgdIds: GROUP_1_PGD_IDS },
  { label: "Antimalarial chemoprophylaxis", pgdIds: GROUP_2_PGD_IDS },
  { label: "Other travel supply", pgdIds: GROUP_3_PGD_IDS },
];

const TRIP_TYPE_LABELS: Record<TripType, string> = {
  holiday: "Holiday",
  "business-trip": "Business trip",
  backpacking: "Backpacking",
  "hotel-stay": "Hotel stay",
  cruise: "Cruise",
  "camping-hostels": "Camping / hostels",
  "living-abroad": "Living abroad (expatriate)",
  safari: "Safari",
  "adventure-travel": "Adventure travel",
  "volunteer-relief-work": "Volunteer or relief work",
  "hajj-umrah": "Pilgrimage (Hajj / Umrah)",
  diving: "Diving",
  "healthcare-worker": "Healthcare worker",
  "medical-dental-treatment-abroad": "Medical or dental treatment abroad",
  "visiting-friends-relatives": "Visiting friends and relatives",
};

const SAFETY_FLAG_META: Partial<
  Record<SafetyFlagKey, { title: string; text: string }>
> = {
  "pregnant-or-breastfeeding": {
    title: "Pregnancy or breastfeeding",
    text: "Avoid all live vaccines: yellow fever, dengue, oral typhoid (Vivotif), and oral cholera (Vaxchora). If typhoid is indicated, use TYPHIM Vi (injectable) not Vivotif. If a malaria-risk area, doxycycline and Malarone are excluded; use mefloquine only if the area is not mefloquine-resistant. If no safe antimalarial exists for the itinerary, refer for specialist advice.",
  },
  immunosuppressed: {
    title: "Immunosuppression or immune condition",
    text: "All live vaccines are contraindicated: yellow fever, dengue, Vivotif, Vaxchora. If yellow fever is required for entry, do not vaccinate — issue a medical exemption letter (designated centre only) and document. Counsel that inactivated vaccines may give a reduced response. Antimalarial choice may require specialist advice.",
  },
  "hajj-umrah": {
    title: "Hajj or Umrah",
    text: "MenACWY proof is required for entry to Saudi Arabia. Confirm the Saudi Ministry of Health time window applies and that the patient will have the certificate before travel.",
  },
  asplenia: {
    title: "Asplenia / hyposplenism",
    text: "NOT the same as immunosuppression \u2014 live vaccines are NOT contraindicated. MenACWY needs a 2-dose primary course (Green Book Ch 7) plus MenB and pneumococcal (refer \u2014 not held). Polysaccharide vaccines give a reduced response. Reinforce the splenic-sepsis safety net.",
  },
};

const RULE_TYPE_LABELS: Record<CoAdminRuleType, string> = {
  timing: "Timing",
  advisory: "Advisory",
  "product-conditional": "Product-conditional",
};

const STEP_TITLES: Record<1 | 2 | 3 | 4 | 5 | 6, string> = {
  1: "NaTHNaC assessment and safety flags",
  2: "Product selection",
  3: "Schedule review",
  4: "Co-administration review and confirm",
  5: "Pre-screening",
  6: "Appointment schedule",
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function toLocalIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDateDDMMYYYY(dateIso: string): string {
  const [year, month, day] = dateIso.split("-");
  if (!year || !month || !day) return dateIso;
  return `${day}/${month}/${year}`;
}

function isDateWithinTravelPeriod(
  dateIso: string,
  departureDateIso: string,
  returnDateIso: string,
): boolean {
  const d = new Date(dateIso + "T00:00:00").getTime();
  const dep = new Date(departureDateIso + "T00:00:00").getTime();
  const ret = new Date(returnDateIso + "T00:00:00").getTime();
  return d > dep && d < ret;
}

function calcAntimalarialSupply(
  pgdId: string,
  brandId: string,
  departureDate: string,
  returnDate: string,
): {
  startDate: string;
  startNote: string;
  afterNote: string;
  totalQtyLabel: string;
  doseNote: string;
} | null {
  const timing = ANTIMALARIAL_BRAND_TIMING[brandId];
  if (!timing) return null;

  const dep = new Date(departureDate + "T00:00:00");
  const ret = new Date(returnDate + "T00:00:00");
  const tripDays = Math.max(
    1,
    Math.round((ret.getTime() - dep.getTime()) / 86400000),
  );

  // Start date
  const startDateObj = new Date(dep);
  startDateObj.setDate(startDateObj.getDate() - timing.daysBefore);
  const startDate = startDateObj.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  // Start note — use exact days unless daysBefore is a clean multiple of 7,
  // since not every regimen's pre-travel start aligns to whole weeks
  // (e.g. mefloquine starts 10 days before departure per its PGD)
  const startNote =
    timing.isWeekly && timing.daysBefore % 7 === 0
      ? "Start " +
        timing.daysBefore / 7 +
        " week" +
        (timing.daysBefore / 7 === 1 ? "" : "s") +
        " before departure"
      : "Start " + timing.daysBefore + " days before departure";

  // After note
  const afterNote =
    timing.isWeekly
      ? "Continue " +
        Math.round(timing.daysAfter / 7) +
        " weeks after leaving risk area"
      : "Continue " + timing.daysAfter + " days after leaving risk area";

  // Quantity
  let totalQtyLabel: string;
  let doseNote: string;

  if (!timing.isWeekly && timing.dailyTablets !== undefined) {
    const totalDays = timing.daysBefore + tripDays + timing.daysAfter;
    const totalTabs = totalDays * timing.dailyTablets;
    const unitLabel =
      totalTabs === 1 ? timing.unit : timing.unit + "s";
    totalQtyLabel = totalTabs + " " + unitLabel;
    doseNote =
      timing.dailyTablets === 1
        ? "Once daily"
        : timing.dailyTablets + " " + timing.unit + "s once daily";
  } else if (timing.isWeekly && timing.weeklyFraction !== undefined) {
    const totalWeeks =
      Math.round(timing.daysBefore / 7) +
      Math.ceil(tripDays / 7) +
      Math.round(timing.daysAfter / 7);
    const totalTabs = Math.ceil(totalWeeks * timing.weeklyFraction);
    totalQtyLabel = totalTabs + " tablet" + (totalTabs === 1 ? "" : "s");
    // Fraction label
    const fractionLabel =
      timing.weeklyFraction === 0.25
        ? "\u00bc tablet"
        : timing.weeklyFraction === 0.5
          ? "\u00bd tablet"
          : timing.weeklyFraction === 0.75
            ? "\u00be tablet"
            : "1 tablet";
    doseNote = fractionLabel + " once weekly";
  } else {
    return null;
  }

  return { startDate, startNote, afterNote, totalQtyLabel, doseNote };
}

function getAntimalarialBrandIdForWeight(
  pgdId: string,
  weightKg: string,
): string {
  const config = getTier2Config(pgdId);
  if (!config) return "";
  if (config.brands.length === 1) return config.brands[0].brandId;
  const weight = parseFloat(weightKg);
  if (isNaN(weight)) return "";
  const match = config.brands.find((b) => {
    if (b.minimumWeightKg !== undefined && weight < b.minimumWeightKg)
      return false;
    if (b.maximumWeightKg !== undefined && weight > b.maximumWeightKg)
      return false;
    return true;
  });
  return match?.brandId ?? "";
}

function normalizeQuestionKey(question: string): string {
  return question
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, "")
    .replace(/ +/g, " ")
    .trim();
}

const NON_SEX_CLINICAL_TERMS = [
  "hepatic",
  "cardiac",
  "conduction",
  "myasthenia",
  "renal",
  "liver",
  "kidney",
  "seizure",
  "epilepsy",
  "hypertension",
  "thyroid",
  "diabetes",
  "allergy",
  "allergic",
  "respiratory",
  "impairment",
  "disturbance",
  "immunocompromised",
  "close contact",
  "live with",
  "infant",
] as const;

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysUntil(dateIso: string): number | null {
  if (!dateIso) return null;
  const target = new Date(`${dateIso}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date(`${todayIsoDate()}T00:00:00`);
  const diffMs = target.getTime() - today.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
}

function buildBasketItem(pgdId: PgdId): BasketItem {
  return {
    pgdId,
    displayName: PRODUCT_META[pgdId].displayName,
    isOffLabel: OFF_LABEL_PGDS.has(pgdId),
    blackTriangle: BLACK_TRIANGLE_PGDS.has(pgdId),
  };
}

function getEligibleSchedules(
  pgdId: string,
  patientAge: number,
  patientWeightKg: string,
): ScheduleOption[] {
  const config = getTier2Config(pgdId);
  if (!config) return [];
  const weightKg = patientWeightKg ? parseFloat(patientWeightKg) : undefined;
  return config.schedules.filter((s) => {
    if (s.minimumAgeYears !== undefined && patientAge < s.minimumAgeYears)
      return false;
    if (s.maximumAgeYears !== undefined && patientAge > s.maximumAgeYears)
      return false;
    if (
      s.minimumWeightKg !== undefined &&
      weightKg !== undefined &&
      weightKg < s.minimumWeightKg
    )
      return false;
    return true;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export function TravelHealthTier1Selection({
  tier1Data,
  patientId,
  patientAge,
  patientSex,
  patientDob,
  siteId,
  isYellowFeverCentre,
  yellowFeverCentreNumber,
  restoredState,
  onAutoSaveSnapshot,
  onComplete,
  onCancel,
}: TravelHealthTier1SelectionProps) {
  const dispatch = useDispatch();
  const { appState } = useTitanApp();
  const { currentUser } = appState || {};
  const pharmacistId = currentUser?.emailAddress ?? "unknown";
  const pharmacistName =
    currentUser?.fullName || currentUser?.name || currentUser?.userName || "Unknown Pharmacist";
  const { initialLocations } = useLocationsSettingsData();
  const createPlan = (planData: any) => {
    const action = dispatch(createPlanAction(planData));
    return (action as any).payload.id;
  };

  const rs = restoredState;
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5 | 6>(
    () => (rs?.step ?? 1) as 1 | 2 | 3 | 4 | 5 | 6,
  );

  // Step 1 state
  const [nathnacConsulted, setNathnacConsulted] = useState(rs?.nathnacConsulted ?? false);
  const [nathnacDestinationsAssessed, setNathnacDestinationsAssessed] =
    useState(rs?.nathnacDestinationsAssessed ?? "");
  const [nathnacVaccinesRecommended, setNathnacVaccinesRecommended] =
    useState(rs?.nathnacVaccinesRecommended ?? "");
  const [
    nathnacMalariaRiskResistanceAgent,
    setNathnacMalariaRiskResistanceAgent,
  ] = useState(rs?.nathnacMalariaRiskResistanceAgent ?? "");
  const [nathnacEntryRequirements, setNathnacEntryRequirements] =
    useState(rs?.nathnacEntryRequirements ?? "");
  const [
    nathnacOtherMedicinesIndicated,
    setNathnacOtherMedicinesIndicated,
  ] = useState(rs?.nathnacOtherMedicinesIndicated ?? "");
  const [acknowledgedFlags, setAcknowledgedFlags] = useState<
    Partial<Record<SafetyFlagKey, SafetyFlagAck>>
  >(rs?.acknowledgedFlags ?? {});

  // Step 1 field-level "touched" state — each required field's error shows
  // as soon as the pharmacist leaves (blurs) it, not only when Next is
  // pressed (which still marks everything touched as a fallback).
  const [nathnacConsultedTouched, setNathnacConsultedTouched] =
    useState(false);
  const [nathnacDestinationsAssessedTouched, setNathnacDestinationsAssessedTouched] =
    useState(false);
  const [nathnacVaccinesRecommendedTouched, setNathnacVaccinesRecommendedTouched] =
    useState(false);
  const [
    nathnacMalariaRiskResistanceAgentTouched,
    setNathnacMalariaRiskResistanceAgentTouched,
  ] = useState(false);
  const [nathnacEntryRequirementsTouched, setNathnacEntryRequirementsTouched] =
    useState(false);
  const [
    nathnacOtherMedicinesIndicatedTouched,
    setNathnacOtherMedicinesIndicatedTouched,
  ] = useState(false);
  const [safetyFlagsTouched, setSafetyFlagsTouched] = useState(false);

  // Step 2 state
  const [basket, setBasket] = useState<BasketItem[]>(rs?.basket ?? []);
  const [dengueGateState, setDengueGateState] =
    useState<DengueGateState>((rs?.dengueGateState as DengueGateState) ?? "idle");
  const [dengueSeropositiveConfirmed, setDengueSeropositiveConfirmed] =
    useState(rs?.dengueSeropositiveConfirmed ?? false);
  const [dengueEvidenceDescription, setDengueEvidenceDescription] =
    useState(rs?.dengueEvidenceDescription ?? "");
  const [menacwyAdvisoryOpen, setMenacwyAdvisoryOpen] = useState(false);
  const [menACWYEntitlementAcknowledged, setMenACWYEntitlementAcknowledged] =
    useState<boolean | undefined>(rs?.menACWYEntitlementAcknowledged);

  // Step 3 state: selected scheduleId per pgdId
  const [selectedSchedules, setSelectedSchedules] = useState<
    Record<string, string>
  >(rs?.selectedSchedules ?? {});
  // Step 3 state: feasibility results from projection engine
  const [feasibility, setFeasibility] = useState<ScheduleFeasibility[]>([]);

  // Step 4 state
  const [coAdminResult, setCoAdminResult] = useState<{
    constraints: CoAdminConstraint[];
  } | null>(null);
  const [coAdminAckChecked, setCoAdminAckChecked] = useState(rs?.coAdminAckChecked ?? false);

  // Step 5 state: pre-screening
  const [prescreenGates, setPrescreenGates] = useState<FlatGate[]>([]);
  const [prescreenExclusions, setPrescreenExclusions] = useState<
    FlatExclusion[]
  >([]);
  const [prescreenCautions, setPrescreenCautions] = useState<FlatCaution[]>(
    [],
  );
  const [prescreenCautionAcknowledged, setPrescreenCautionAcknowledged] =
    useState(rs?.prescreenCautionAcknowledged ?? false);
  const [prescreenRemovedProducts, setPrescreenRemovedProducts] = useState<
    { productName: string; reason: string }[]
  >([]);
  const [prescreenComplete, setPrescreenComplete] = useState(rs?.prescreenComplete ?? false);

  // Step 6 state — appointment schedule review
  const [projectedSlots, setProjectedSlots] = useState<
    AppointmentSlotWithTime[]
  >([]);
  const [noFitWarnings, setNoFitWarnings] = useState<string[]>([]);
  const [editingSlotIndex, setEditingSlotIndex] = useState<number | null>(
    null,
  );
  const [slotDateEditError, setSlotDateEditError] = useState("");
  const [intervalViolations, setIntervalViolations] = useState<
    IntervalViolation[]
  >([]);
  const [intervalOverrideReason, setIntervalOverrideReason] = useState("");
  const [pickingTimeForSlotIndex, setPickingTimeForSlotIndex] = useState<
    number | null
  >(null);
  const [supplyTaskDates, setSupplyTaskDates] = useState<
    Record<string, string>
  >({});

  // ── Auto-save snapshot ────────────────────────────────────────────────────
  const onAutoSaveSnapshotRef = useRef(onAutoSaveSnapshot);
  onAutoSaveSnapshotRef.current = onAutoSaveSnapshot;

  useEffect(() => {
    if (!onAutoSaveSnapshotRef.current) return;
    onAutoSaveSnapshotRef.current({
      step,
      nathnacConsulted,
      nathnacDestinationsAssessed,
      nathnacVaccinesRecommended,
      nathnacMalariaRiskResistanceAgent,
      nathnacEntryRequirements,
      nathnacOtherMedicinesIndicated,
      acknowledgedFlags,
      basket,
      dengueGateState,
      dengueSeropositiveConfirmed,
      dengueEvidenceDescription,
      menACWYEntitlementAcknowledged,
      selectedSchedules,
      coAdminAckChecked,
      prescreenCautionAcknowledged,
      prescreenComplete,
    });
  }, [
    step,
    nathnacConsulted,
    nathnacDestinationsAssessed,
    nathnacVaccinesRecommended,
    nathnacMalariaRiskResistanceAgent,
    nathnacEntryRequirements,
    nathnacOtherMedicinesIndicated,
    acknowledgedFlags,
    basket,
    dengueGateState,
    dengueSeropositiveConfirmed,
    dengueEvidenceDescription,
    menACWYEntitlementAcknowledged,
    selectedSchedules,
    coAdminAckChecked,
    prescreenCautionAcknowledged,
    prescreenComplete,
  ]);

  const isYellowFeverAvailable =
    isYellowFeverCentre === true && yellowFeverCentreNumber.trim().length > 0;

  const triggeredFlags = useMemo<SafetyFlagKey[]>(() => {
    const flags: SafetyFlagKey[] = [];
    if (
      tier1Data.isPregnant === true ||
      tier1Data.couldBePregnant === true ||
      tier1Data.isBreastfeeding === true
    ) {
      flags.push("pregnant-or-breastfeeding");
    }
    if (tier1Data.hasImmuneCondition === true) {
      flags.push("immunosuppressed");
    }
    if (tier1Data.tripTypes.includes("hajj-umrah")) {
      flags.push("hajj-umrah");
    }
    if (tier1Data.medicalHistory?.spleenProblemsOrNoSpleen === true) {
      flags.push("asplenia");
    }
    return flags;
  }, [tier1Data]);

  const leadTimeDays = useMemo(
    () => daysUntil(tier1Data.departureDate),
    [tier1Data.departureDate],
  );

  // ── Step 1 helpers ──────────────────────────────────────────────────────

  const handleFlagAckChange = (flagKey: SafetyFlagKey, checked: boolean) => {
    setAcknowledgedFlags((prev) => {
      const next = { ...prev };
      if (checked) {
        next[flagKey] = {
          flagKey,
          acknowledgedAt: new Date().toISOString(),
          acknowledgedBy: pharmacistId,
        };
      } else {
        delete next[flagKey];
      }
      return next;
    });
  };

  const isStep1Valid = (): boolean => {
    if (!nathnacConsulted) return false;
    if (
      !nathnacDestinationsAssessed.trim() ||
      !nathnacVaccinesRecommended.trim() ||
      !nathnacMalariaRiskResistanceAgent.trim() ||
      !nathnacEntryRequirements.trim() ||
      !nathnacOtherMedicinesIndicated.trim()
    ) {
      return false;
    }
    return triggeredFlags.every(
      (flagKey) => acknowledgedFlags[flagKey] !== undefined,
    );
  };

  // ── Step 2 helpers ──────────────────────────────────────────────────────

  const isInBasket = (pgdId: PgdId): boolean =>
    basket.some((item) => item.pgdId === pgdId);

  const toggleGenericProduct = (pgdId: PgdId) => {
    setBasket((prev) =>
      prev.some((item) => item.pgdId === pgdId)
        ? prev.filter((item) => item.pgdId !== pgdId)
        : [...prev, buildBasketItem(pgdId)],
    );
  };

  const handleDengueTileClick = () => {
    if (dengueGateState === "idle") {
      setDengueGateState("confirming");
      return;
    }
    if (dengueGateState === "confirmed") {
      setBasket((prev) => prev.filter((item) => item.pgdId !== PGD_IDS.DENGUE));
      setDengueGateState("idle");
      setDengueSeropositiveConfirmed(false);
    }
    // 'declined' state: tile is permanently disabled, no handler wired.
  };

  const handleDengueConfirm = () => {
    setDengueGateState("confirmed");
    setDengueSeropositiveConfirmed(true);
    setBasket((prev) => [...prev, buildBasketItem(PGD_IDS.DENGUE)]);
  };

  const handleDengueDecline = () => {
    setDengueGateState("declined");
  };

  const handleMenacwyToggle = () => {
    if (isInBasket(PGD_IDS.MENACWY)) {
      setBasket((prev) =>
        prev.filter((item) => item.pgdId !== PGD_IDS.MENACWY),
      );
      setMenacwyAdvisoryOpen(false);
      return;
    }
    if (patientAge < 25) {
      setMenacwyAdvisoryOpen((prev) => !prev);
      return;
    }
    setBasket((prev) => [...prev, buildBasketItem(PGD_IDS.MENACWY)]);
  };

  const handleMenacwyAdvisoryConfirm = () => {
    setBasket((prev) => [...prev, buildBasketItem(PGD_IDS.MENACWY)]);
    setMenACWYEntitlementAcknowledged(true);
    setMenacwyAdvisoryOpen(false);
  };

  const isStep2Valid = (): boolean => basket.length > 0;

  const renderGenericTile = (pgdId: PgdId) => {
    const meta = PRODUCT_META[pgdId];
    const selected = isInBasket(pgdId);
    const isBlackTriangle = BLACK_TRIANGLE_PGDS.has(pgdId);
    const isFemaleOnly = FEMALE_ONLY_PGDS.has(pgdId);
    const sexIneligible = isFemaleOnly && patientSex === "Male";
    const selectedAntimalarial = basket.find((b) =>
      ANTIMALARIAL_PGDS.has(b.pgdId),
    );
    const antimalarialBlocked =
      ANTIMALARIAL_PGDS.has(pgdId as PgdId) &&
      selectedAntimalarial !== undefined &&
      selectedAntimalarial.pgdId !== pgdId;
    return (
      <button
        key={pgdId}
        type="button"
        onClick={() => toggleGenericProduct(pgdId)}
        disabled={sexIneligible || antimalarialBlocked}
        className={`w-full text-left border rounded-lg px-4 py-3 flex items-center justify-between transition-colors ${
          sexIneligible ? "opacity-40 " : ""
        }${antimalarialBlocked ? "opacity-40 cursor-not-allowed " : ""}${
          selected
            ? "border-slate-900 bg-slate-50"
            : "border-gray-200 bg-white hover:bg-gray-50"
        }`}
      >
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-900">
            {meta.displayName}
            {isBlackTriangle ? " \u25BC" : ""}
          </span>
          {sexIneligible && (
            <p className="text-xs text-gray-500">Not indicated for this patient</p>
          )}
          {antimalarialBlocked && (
            <p className="text-xs text-gray-500">
              Remove {selectedAntimalarial?.displayName} to select a
              different antimalarial
            </p>
          )}
        </div>
        {selected && <CheckCircle2 className="w-4 h-4 text-slate-900" />}
      </button>
    );
  };

  const renderYellowFeverTile = () => {
    const meta = PRODUCT_META[PGD_IDS.YELLOW_FEVER];
    if (!isYellowFeverAvailable) {
      return (
        <div
          key={PGD_IDS.YELLOW_FEVER}
          className="border rounded-lg px-4 py-3 bg-gray-100"
        >
          <span className="text-sm font-medium text-gray-400">
            {meta.displayName}
          </span>
          <p className="text-xs text-red-600 mt-1">
            Refer — this site is not configured as a designated Yellow Fever
            Vaccination Centre.
          </p>
        </div>
      );
    }
    return renderGenericTile(PGD_IDS.YELLOW_FEVER);
  };

  const renderDengueTile = () => {
    const meta = PRODUCT_META[PGD_IDS.DENGUE];

    if (dengueGateState === "confirming") {
      return (
        <div
          key={PGD_IDS.DENGUE}
          className="border border-amber-300 bg-amber-50 rounded-lg p-4 space-y-3 sm:col-span-2"
        >
          <p className="font-medium text-gray-900">
            Confirm prior dengue infection
          </p>
          <p className="text-sm text-gray-700">
            Qdenga (dengue vaccine) may only be given to individuals with
            documented prior dengue infection (seropositive).
            Administration to seronegative or unknown-status individuals is
            not permitted under this PGD.
          </p>
          <p className="text-sm font-medium text-gray-900">
            Does this patient have documented prior dengue infection?
          </p>
          <div className="border border-gray-300 bg-white rounded p-3 space-y-2">
            <p className="text-xs text-gray-600">
              Self-report alone does not meet this requirement. Ask to see or
              confirm documented evidence — for example a prior NHS lab
              result, a GP letter, or hospital discharge records confirming a
              past dengue diagnosis. If you cannot verify this, do not
              proceed — refer the patient to explore serology testing instead.
            </p>
            <Label htmlFor="dengue-evidence">
              Describe the evidence reviewed
            </Label>
            <Textarea
              id="dengue-evidence"
              value={dengueEvidenceDescription}
              onChange={(e) => setDengueEvidenceDescription(e.target.value)}
              rows={2}
              placeholder="e.g. GP letter dated [date] confirming dengue diagnosis in [year]"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={handleDengueConfirm}
              disabled={!dengueEvidenceDescription.trim()}
              className="bg-slate-900 hover:bg-slate-800"
            >
              Yes — confirmed seropositive
            </Button>
            <Button type="button" variant="outline" onClick={handleDengueDecline}>
              No / unknown — do not vaccinate
            </Button>
          </div>
        </div>
      );
    }

    if (dengueGateState === "declined") {
      return (
        <div
          key={PGD_IDS.DENGUE}
          className="border rounded-lg px-4 py-3 bg-gray-100"
        >
          <span className="text-sm font-medium text-gray-400">
            {meta.displayName} {"\u25BC"}
          </span>
          <p className="text-xs text-red-600 mt-1">
            Dengue vaccine cannot be supplied. Refer if the patient wishes to
            explore serology testing.
          </p>
        </div>
      );
    }

    const selected = dengueGateState === "confirmed";
    return (
      <button
        key={PGD_IDS.DENGUE}
        type="button"
        onClick={handleDengueTileClick}
        className={`w-full text-left border rounded-lg px-4 py-3 flex items-center justify-between transition-colors ${
          selected
            ? "border-slate-900 bg-slate-50"
            : "border-gray-200 bg-white hover:bg-gray-50"
        }`}
      >
        <span className="text-sm font-medium text-gray-900">
          {meta.displayName} {"\u25BC"}
        </span>
        {selected && <CheckCircle2 className="w-4 h-4 text-slate-900" />}
      </button>
    );
  };

  const renderMenacwyTile = () => {
    const meta = PRODUCT_META[PGD_IDS.MENACWY];
    const selected = isInBasket(PGD_IDS.MENACWY);
    return (
      <div key={PGD_IDS.MENACWY} className="space-y-2">
        <button
          type="button"
          onClick={handleMenacwyToggle}
          className={`w-full text-left border rounded-lg px-4 py-3 flex items-center justify-between transition-colors ${
            selected
              ? "border-slate-900 bg-slate-50"
              : "border-gray-200 bg-white hover:bg-gray-50"
          }`}
        >
          <span className="text-sm font-medium text-gray-900">
            {meta.displayName}
          </span>
          {selected && <CheckCircle2 className="w-4 h-4 text-slate-900" />}
        </button>
        {menacwyAdvisoryOpen && (
          <Alert>
            <AlertDescription className="space-y-3">
              <p>
                This patient may be entitled to free MenACWY from an NHS
                provider. Confirm they have been made aware before proceeding
                with this private service.
              </p>
              <Button
                type="button"
                onClick={handleMenacwyAdvisoryConfirm}
                className="bg-slate-900 hover:bg-slate-800"
              >
                Confirmed — patient has been advised
              </Button>
            </AlertDescription>
          </Alert>
        )}
      </div>
    );
  };

  const renderRifaximinTile = () => {
    const meta = PRODUCT_META[PGD_IDS.RIFAXIMIN];
    const selected = isInBasket(PGD_IDS.RIFAXIMIN);
    return (
      <button
        key={PGD_IDS.RIFAXIMIN}
        type="button"
        onClick={() => toggleGenericProduct(PGD_IDS.RIFAXIMIN)}
        className={`w-full text-left border rounded-lg px-4 py-3 flex items-center justify-between transition-colors ${
          selected
            ? "border-slate-900 bg-slate-50"
            : "border-gray-200 bg-white hover:bg-gray-50"
        }`}
      >
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-900">
            {meta.displayName}
          </span>
          <p className="text-xs text-gray-500 mt-1">
            Treatment only &mdash; for patients currently experiencing
            travellers&rsquo; diarrhoea. Red-flag screening is completed at
            supply.
          </p>
        </div>
        {selected && <CheckCircle2 className="w-4 h-4 text-slate-900 shrink-0" />}
      </button>
    );
  };

  const renderProductTile = (pgdId: PgdId) => {
    if (pgdId === PGD_IDS.YELLOW_FEVER) return renderYellowFeverTile();
    if (pgdId === PGD_IDS.DENGUE) return renderDengueTile();
    if (pgdId === PGD_IDS.MENACWY) return renderMenacwyTile();
    if (pgdId === PGD_IDS.RIFAXIMIN) return renderRifaximinTile();
    return renderGenericTile(pgdId);
  };

  // ── Step 3 helpers ──────────────────────────────────────────────────────

  const runFeasibility = (schedules: Record<string, string>) => {
    const activeSite = initialLocations.find((loc) => loc.id === siteId);
    const travelHealthRoom = activeSite?.rooms.find((room) =>
      room.services.includes("Travel Health Consultation"),
    );
    const siteAvailability: AvailabilityMap =
      travelHealthRoom?.availability ?? {};

    const products = basket.flatMap((item) => {
      const scheduleId = schedules[item.pgdId];
      const config = getTier2Config(item.pgdId);
      if (!config || !scheduleId) return [];
      const schedule = config.schedules.find(
        (s) => s.scheduleId === scheduleId,
      );
      if (!schedule) return [];
      return [
        {
          pgdId: item.pgdId,
          productName: item.displayName,
          selectedScheduleId: schedule.scheduleId,
          selectedScheduleLabel: schedule.label,
          selectedScheduleOffLabel: schedule.offLabel,
          doses: schedule.doses,
          serviceType: config.serviceType,
          blackTriangle: item.blackTriangle,
        },
      ];
    });

    if (products.length === 0) {
      setFeasibility([]);
      return;
    }

    const acceleratedAlternatives = basket.flatMap((item) => {
      const config = getTier2Config(item.pgdId);
      if (!config) return [];
      const currentScheduleId = schedules[item.pgdId];
      const eligible = getEligibleSchedules(item.pgdId, patientAge, "");
      const current = eligible.find(
        (s) => s.scheduleId === currentScheduleId,
      );
      const currentLastInterval = current
        ? Math.max(0, ...current.doses.map((d) => d.intervalFromDose1Days ?? 0))
        : Infinity;
      const faster = eligible.find(
        (s) =>
          s.scheduleId !== currentScheduleId &&
          Math.max(0, ...s.doses.map((d) => d.intervalFromDose1Days ?? 0)) <
            currentLastInterval,
      );
      if (!faster) return [];
      return [
        {
          pgdId: item.pgdId,
          scheduleId: faster.scheduleId,
          scheduleLabel: faster.label,
          doses: faster.doses,
        },
      ];
    });

    const today = new Date().toISOString().split("T")[0];

    const result = projectTravelHealthSchedule({
      startDate: today,
      departureDate: tier1Data.departureDate,
      products,
      siteAvailability,
      members: initialMembers.map((m) => ({
        id: m.id,
        isClinician: m.isClinician,
        active: m.active,
        servicesProvided: m.servicesProvided,
        availability: m.availability as unknown as AvailabilityMap | undefined,
      })),
      acceleratedAlternatives,
    });

    setFeasibility(result.feasibility);
  };

  // ── Step 4 helpers ──────────────────────────────────────────────────────

  const isStep3ConfirmEnabled = (): boolean => {
    if (!coAdminResult) return false;
    if (coAdminResult.constraints.length === 0) return true;
    return coAdminAckChecked;
  };

  // ── Step 5 helpers ──────────────────────────────────────────────────────

  const getAutoAnswerForQuestion = (
    question: string,
  ): { answer: "yes" | "no"; source: string } | undefined => {
    const q = question.toLowerCase();

    const patientAgeMonths = patientDob
      ? Math.floor(
          (Date.now() - new Date(patientDob).getTime()) /
            (1000 * 60 * 60 * 24 * 30.4375),
        )
      : null;

    if (
      patientAgeMonths !== null &&
      !isNaN(patientAgeMonths) &&
      (q.includes("under 2 month") ||
        q.includes("less than 2 month") ||
        q.includes("younger than 2 month") ||
        q.includes("2 months of age") ||
        q.includes("under two month"))
    ) {
      const ageLabel =
        patientAgeMonths !== null && patientAgeMonths >= 24
          ? Math.floor(patientAgeMonths / 12) + " years"
          : patientAgeMonths + " month" + (patientAgeMonths === 1 ? "" : "s");
      return {
        answer: patientAgeMonths < 2 ? "yes" : "no",
        source: "patient age: " + ageLabel + " (from demographics)",
      };
    }

    const hasAgeContext =
      q.includes("year") ||
      q.includes("of age") ||
      q.includes("aged");

    if (hasAgeContext) {
      // "aged X–Y" / "aged X to Y": patient in range check
      const ageRangeMatch = q.match(
        /aged\s+(\d+)\s*(?:\u2013|-|to)\s*(\d+)/,
      );
      if (ageRangeMatch) {
        const lo = parseInt(ageRangeMatch[1]);
        const hi = parseInt(ageRangeMatch[2]);
        if (patientAge < lo || patientAge > hi) {
          return {
            answer: "no",
            source:
              "patient age: " +
              patientAge +
              " years (from demographics)",
          };
        }
        return undefined;
      }

      // "under X" / "less than X" / "below X years"
      const ageUnderMatch = q.match(
        /(?:under|less than|below)\s+(\d+)\s*(?:years?)?(?:\s+of\s+age)?/,
      );
      if (ageUnderMatch) {
        const threshold = parseInt(ageUnderMatch[1]);
        return {
          answer: patientAge < threshold ? "yes" : "no",
          source:
            "patient age: " + patientAge + " years (from demographics)",
        };
      }

      // "over X" / "older than X" / "aged X or over" /
      // "X or over" / "X or older" / "X years of age or over"
      const ageOverMatch = q.match(
        /(?:over|older than|aged)\s+(\d+)|(\d+)\s*(?:years?)?\s*(?:of\s+age\s+)?or\s+(?:over|older)/,
      );
      if (ageOverMatch) {
        const threshold = parseInt(
          ageOverMatch[1] ?? ageOverMatch[2],
        );
        return {
          answer: patientAge >= threshold ? "yes" : "no",
          source:
            "patient age: " + patientAge + " years (from demographics)",
        };
      }
    }

    const weightKg = parseFloat(tier1Data.weightKg);
    if (!isNaN(weightKg)) {
      const underMatch = q.match(
        /(?:under|less than|below)\s+(\d+(?:\.\d+)?)\s*kg/,
      );
      if (underMatch) {
        const threshold = parseFloat(underMatch[1]);
        return {
          answer: weightKg < threshold ? "yes" : "no",
          source:
            "patient weight: " + weightKg + "kg (from health screen)",
        };
      }
      const overMatch = q.match(
        /(?:over|more than|above)\s+(\d+(?:\.\d+)?)\s*kg/,
      );
      if (overMatch) {
        const threshold = parseFloat(overMatch[1]);
        return {
          answer: weightKg > threshold ? "yes" : "no",
          source:
            "patient weight: " + weightKg + "kg (from health screen)",
        };
      }
    }

    const hasSexOnlyContent = !NON_SEX_CLINICAL_TERMS.some((term) =>
      q.includes(term),
    );

    if (
      patientSex === "Male" &&
      hasSexOnlyContent &&
      (q.includes("pregnant") ||
        q.includes("breastfeed") ||
        q.includes("period delay"))
    ) {
      return { answer: "no", source: "patient sex recorded as Male" };
    }

    if (
      q.includes("pregnant") ||
      q.includes("pregnancy") ||
      q.includes("breastfeed")
    ) {
      const isYes =
        tier1Data.isPregnant === true ||
        tier1Data.couldBePregnant === true ||
        tier1Data.isBreastfeeding === true ||
        tier1Data.planningPregnancy === true;
      const isDefinitelyNo =
        tier1Data.isPregnant === false &&
        tier1Data.couldBePregnant === false &&
        tier1Data.isBreastfeeding === false &&
        tier1Data.planningPregnancy === false;
      if (isYes)
        return {
          answer: "yes",
          source: "pregnancy / breastfeeding status from health screen",
        };
      if (isDefinitelyNo)
        return {
          answer: "no",
          source: "pregnancy / breastfeeding status from health screen",
        };
    }

    if (
      q.includes("immunosuppressed") ||
      (q.includes("immune") && q.includes("system"))
    ) {
      if (tier1Data.hasImmuneCondition !== undefined) {
        return {
          answer: tier1Data.hasImmuneCondition ? "yes" : "no",
          source: "immune condition status from health screen",
        };
      }
    }

    // ── Drug-class hypersensitivity from allergy free text ──────
    // Narrow, AHT-endorsed exception: Q25's free-text allergy detail
    // is checked for three specific drug-class mentions, each tied to
    // a specific product's own hypersensitivity exclusion question.
    const allergyDetail = (tier1Data.allergies?.detail ?? "").toLowerCase();

    const TETRACYCLINE_TERMS = [
      "tetracycline", "doxycycline", "minocycline",
      "oxytetracycline", "lymecycline",
    ];
    const QUININE_TERMS = ["quinine", "quinidine"];
    const RIFAMYCIN_TERMS = [
      "rifampicin", "rifabutin", "rifapentine", "rifaximin", "rifamycin",
    ];

    const mentionsAny = (terms: string[]) =>
      terms.some((term) => allergyDetail.includes(term));

    if (
      q.includes("hypersensitivity") &&
      q.includes("tetracycline") &&
      mentionsAny(TETRACYCLINE_TERMS)
    ) {
      return {
        answer: "yes",
        source: "allergy detail mentions a tetracycline-class drug",
      };
    }

    if (
      q.includes("hypersensitivity") &&
      (q.includes("quinine") || q.includes("quinidine")) &&
      mentionsAny(QUININE_TERMS)
    ) {
      return {
        answer: "yes",
        source: "allergy detail mentions quinine/quinidine",
      };
    }

    if (
      q.includes("hypersensitivity") &&
      q.includes("rifamycin") &&
      mentionsAny(RIFAMYCIN_TERMS)
    ) {
      return {
        answer: "yes",
        source: "allergy detail mentions a rifamycin-class drug",
      };
    }

    return undefined;
  };

  const HIV_EXPLAINER_QUESTIONS = new Set([
    "Does the patient have symptomatic HIV infection, or asymptomatic HIV with evidence of impaired immune function?",
    "Does the patient have symptomatic HIV infection, or asymptomatic HIV with impaired immune function, a congenital immune deficiency, or acquired immune deficiency?",
  ]);

  const getHivExplainerText = (question: string): string | undefined => {
    if (!HIV_EXPLAINER_QUESTIONS.has(question)) return undefined;
    const threshold =
      "AHT threshold: \u2265200 (ideally \u2265500) on stable ART with undetectable viral load \u2014 live vaccines may proceed with a documented risk assessment. <200 \u2014 contraindicated.";
    return tier1Data.hivCd4Count
      ? "Patient-reported CD4: " +
          tier1Data.hivCd4Count +
          " cells/\u00b5L. " +
          threshold
      : "No CD4 count recorded. Ask the patient or check with their HIV specialist if needed to inform this assessment. " +
          threshold;
  };

  const buildPrescreenItems = () => {
    const gateMap = new Map<string, FlatGate>();
    const exclusionMap = new Map<string, FlatExclusion>();
    const cautionMap = new Map<string, FlatCaution>();

    for (const item of basket) {
      const config = getTier2Config(item.pgdId);
      if (!config) continue;

      for (const gate of config.supplyGates) {
        const key = gate.trim();
        if (gateMap.has(key)) {
          gateMap.get(key)!.productNames.push(item.displayName);
        } else {
          gateMap.set(key, {
            key,
            text: gate,
            productNames: [item.displayName],
            confirmed: false,
          });
        }
      }

      for (const check of config.exclusions) {
        const key = normalizeQuestionKey(check.question);
        const auto = getAutoAnswerForQuestion(check.question);
        if (exclusionMap.has(key)) {
          exclusionMap.get(key)!.products.push({
            pgdId: item.pgdId,
            productName: item.displayName,
            checkId: check.checkId,
            disqualifyingAnswer: check.disqualifyingAnswer,
            disqualificationMessage: check.disqualificationMessage,
            referralMessage: check.referralMessage,
          });
        } else {
          exclusionMap.set(key, {
            key,
            question: check.question,
            products: [
              {
                pgdId: item.pgdId,
                productName: item.displayName,
                checkId: check.checkId,
                disqualifyingAnswer: check.disqualifyingAnswer,
                disqualificationMessage: check.disqualificationMessage,
                referralMessage: check.referralMessage,
              },
            ],
            answer: auto?.answer,
            autoAnswered: auto !== undefined,
            autoAnswerSource: auto?.source,
            explainerText: getHivExplainerText(check.question),
          });
        }
      }

      for (const caution of config.cautions) {
        const key = normalizeQuestionKey(caution.question);
        const auto = getAutoAnswerForQuestion(caution.question);
        if (cautionMap.has(key)) {
          cautionMap.get(key)!.products.push({
            pgdId: item.pgdId,
            productName: item.displayName,
            checkId: caution.checkId,
            cautionMessage: caution.cautionMessage,
          });
        } else {
          cautionMap.set(key, {
            key,
            question: caution.question,
            products: [
              {
                pgdId: item.pgdId,
                productName: item.displayName,
                checkId: caution.checkId,
                cautionMessage: caution.cautionMessage,
              },
            ],
            answer: auto?.answer,
            autoAnswered: auto !== undefined,
            autoAnswerSource: auto?.source,
          });
        }
      }
    }

    setPrescreenGates(Array.from(gateMap.values()));
    setPrescreenExclusions(Array.from(exclusionMap.values()));
    setPrescreenCautions(Array.from(cautionMap.values()));
    setPrescreenCautionAcknowledged(false);
    setPrescreenRemovedProducts([]);
    setPrescreenComplete(false);
  };

  const handleConfirmPrescreen = () => {
    if (prescreenGates.some((g) => !g.confirmed)) return;
    if (prescreenExclusions.some((e) => e.answer === undefined)) return;
    if (prescreenCautions.some((c) => c.answer === undefined)) return;
    const triggered = prescreenCautions.filter((c) => c.answer === "yes");
    if (triggered.length > 0 && !prescreenCautionAcknowledged) return;

    const removed: { productName: string; reason: string }[] = [];
    const passing = basket.filter((item) => {
      const config = getTier2Config(item.pgdId);
      if (!config) return true;
      for (const check of config.exclusions) {
        const flat = prescreenExclusions.find(
          (e) => e.key === normalizeQuestionKey(check.question),
        );
        if (!flat) continue;
        if (flat.answer === check.disqualifyingAnswer) {
          removed.push({
            productName: item.displayName,
            reason: check.disqualificationMessage,
          });
          return false;
        }
      }
      return true;
    });

    setBasket(passing);
    setPrescreenRemovedProducts(removed);
    setPrescreenComplete(true);
  };

  const isStep5Valid = (): boolean => prescreenComplete;
  const siteOpenDays = useMemo(() => {
    const activeSite = initialLocations.find((loc) => loc.id === siteId);
    const travelRoom = activeSite?.rooms.find((room) =>
      room.services.includes("Travel Health Consultation"),
    );
    if (!travelRoom?.availability) return null;
    const days = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ] as const;
    const openDays = days
      .filter((day) => travelRoom.availability[day]?.enabled)
      .map((day) => day.charAt(0).toUpperCase() + day.slice(1));
    return openDays.length > 0 ? openDays.join(", ") : null;
  }, [siteId]);

  function getSchedulingMembers(): SchedulingMember[] {
    return initialMembers.map((m) => ({
      id: m.id,
      isClinician: m.isClinician,
      active: m.active,
      servicesProvided: m.servicesProvided,
      availability: m.availability as unknown as
        | AvailabilityMap
        | undefined,
    }));
  }

  const handleSlotDateChange = (
    slotIndex: number,
    newDate: string,
  ) => {
    const oldSlot = projectedSlots[slotIndex];
    if (!oldSlot) return;

    const oldMs = new Date(oldSlot.date + "T00:00:00").getTime();
    const newMs = new Date(newDate + "T00:00:00").getTime();
    const deltaDays = Math.round((newMs - oldMs) / 86400000);
    const members = getSchedulingMembers();

    const newSlots = projectedSlots.map((slot, i) => {
      if (i < slotIndex) return slot;

      const targetDate =
        i === slotIndex
          ? newDate
          : (() => {
              const shifted = new Date(
                new Date(slot.date + "T00:00:00").getTime() +
                  deltaDays * 86400000,
              );
              return toLocalIsoDate(shifted);
            })();

      const dateObj = new Date(targetDate + "T00:00:00");
      const availablePharmacistIds = getQualifiedPharmacists(
        targetDate,
        members,
      );
      return {
        ...slot,
        date: targetDate,
        dayOfWeek: dateObj.toLocaleDateString("en-GB", {
          weekday: "long",
        }),
        availablePharmacistIds,
        noQualifiedPharmacistWarning: availablePharmacistIds.length === 0,
      };
    });

    setProjectedSlots(newSlots);
    setIntervalViolations(
      checkIntervalViolations(newSlots, buildScheduledProducts()),
    );
    setIntervalOverrideReason("");
    setEditingSlotIndex(null);
  };

  function buildScheduledProducts() {
    return basket
      .filter((item) => !ANTIMALARIAL_PGDS.has(item.pgdId))
      .flatMap((item) => {
        const scheduleId = selectedSchedules[item.pgdId];
        const config = getTier2Config(item.pgdId);
        if (!config || !scheduleId) return [];
        const schedule = config.schedules.find(
          (s) => s.scheduleId === scheduleId,
        );
        if (!schedule) return [];
        return [
          {
            pgdId: item.pgdId,
            productName: item.displayName,
            selectedScheduleId: schedule.scheduleId,
            selectedScheduleLabel: schedule.label,
            selectedScheduleOffLabel: schedule.offLabel,
            doses: schedule.doses,
            serviceType: config.serviceType,
            blackTriangle: item.blackTriangle,
          },
        ];
      });
  }

  const handleSlotTimeSelected = (
    slotIndex: number,
    selected: {
      time: string;
      date: Date;
      service?: string;
      serviceId?: string;
      practitioner?: string;
      practitionerId?: string;
    },
  ) => {
    const selectedIsoDate = toLocalIsoDate(selected.date);
    const availablePharmacistIds = getQualifiedPharmacists(
      selectedIsoDate,
      getSchedulingMembers(),
    );
    setProjectedSlots((prev) =>
      prev.map((slot, i) =>
        i === slotIndex
          ? {
              ...slot,
              date: selectedIsoDate,
              dayOfWeek: selected.date.toLocaleDateString("en-GB", {
                weekday: "long",
              }),
              selectedTime: selected.time,
              selectedPharmacistName: selected.practitioner,
              selectedPharmacistId: selected.practitionerId,
              selectedServiceName: selected.service,
              selectedServiceId: selected.serviceId,
              availablePharmacistIds,
              noQualifiedPharmacistWarning: availablePharmacistIds.length === 0,
            }
          : slot,
      ),
    );
    setSlotDateEditError("");
    setPickingTimeForSlotIndex(null);
  };

  const handleRunProjection = () => {
    if (!coAdminResult) return;
    if (coAdminResult.constraints.length > 0 && !coAdminAckChecked) return;
    const finalProducts = buildScheduledProducts();

    const activeSite = initialLocations.find((loc) => loc.id === siteId);
    const travelRoom = activeSite?.rooms.find((room) =>
      room.services.includes("Travel Health Consultation"),
    );
    const siteAvailability: AvailabilityMap = travelRoom?.availability ?? {};

    const today = new Date().toISOString().split("T")[0];

    const result =
      finalProducts.length > 0
        ? projectTravelHealthSchedule({
            startDate: today,
            departureDate: tier1Data.departureDate,
            products: finalProducts,
            siteAvailability,
            members: initialMembers.map((m) => ({
              id: m.id,
              isClinician: m.isClinician,
              active: m.active,
              servicesProvided: m.servicesProvided,
              availability: m.availability as unknown as
                | AvailabilityMap
                | undefined,
            })),
          })
        : {
            appointmentSlots: [],
            feasibility: [],
            noFitWarnings: [],
            adjustmentLog: [],
          };

    setProjectedSlots(result.appointmentSlots);
    setNoFitWarnings(result.noFitWarnings ?? []);
    setIntervalViolations(
      checkIntervalViolations(result.appointmentSlots, finalProducts),
    );
    setIntervalOverrideReason("");
    setStep(6);
  };

  const handleCreatePlan = () => {

    if (!coAdminResult) return;
    if (coAdminResult.constraints.length > 0 && !coAdminAckChecked) return;

    const nowIso = new Date().toISOString();
    const basketProducts: TravelHealthPlanProduct[] = basket.map((item) => ({
      id: generateId("TRAVELPRODUCT"),
      pgdId: item.pgdId,
      productName: item.displayName,
      status: "planned" as const,
      doseSchedule: [],
      currentDoseNumber: 0,
      priorDoseHistory: [],
      offLabel: item.isOffLabel,
      blackTriangle: item.blackTriangle,
      auditRecordIds: [],
      addedAt: nowIso,
      prescreenCompleted: true,
    }));

    const planId = createPlan({
      patientId,
      siteId,
      tripDestinations: tier1Data.tripDestinations,
      tripTypes: tier1Data.tripTypes,
      activities: tier1Data.activities,
      departureDate: tier1Data.departureDate,
      returnDate: tier1Data.returnDate,
      tripNotes: tier1Data.tripNotes,
      nathnacConsulted: nathnacConsulted,
      nathnacDestinationsAssessed: nathnacDestinationsAssessed,
      nathnacVaccinesRecommended: nathnacVaccinesRecommended,
      nathnacMalariaRiskResistanceAgent: nathnacMalariaRiskResistanceAgent,
      nathnacEntryRequirements: nathnacEntryRequirements,
      nathnacOtherMedicinesIndicated: nathnacOtherMedicinesIndicated,
      dengueSeropositiveConfirmed,
      dengueEvidenceDescription:
        dengueGateState === "confirmed" ? dengueEvidenceDescription : "",
      scheduleOverrideReason:
        intervalViolations.length > 0 ? intervalOverrideReason : undefined,
      basket: basketProducts,
      safetyFlagAcknowledgements: Object.values(acknowledgedFlags),
      coAdminMatrixOutput: coAdminResult.constraints,
      menACWYEntitlementAcknowledged,
      status: "active" as const,
      createdBy: pharmacistId,
      createdByName: pharmacistName,
    });

    onComplete(planId, projectedSlots, supplyTaskDates);
  };

  // ── Navigation ───────────────────────────────────────────────────────────

  const handleNext = () => {
    if (step === 1) {
      if (!isStep1Valid()) {
        setNathnacConsultedTouched(true);
        setNathnacDestinationsAssessedTouched(true);
        setNathnacVaccinesRecommendedTouched(true);
        setNathnacMalariaRiskResistanceAgentTouched(true);
        setNathnacEntryRequirementsTouched(true);
        setNathnacOtherMedicinesIndicatedTouched(true);
        setSafetyFlagsTouched(true);
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      if (!isStep2Valid()) return;
      const initialSchedules: Record<string, string> = {};
      basket.forEach((item) => {
        const eligible = getEligibleSchedules(item.pgdId, patientAge, "");
        if (eligible.length > 0) {
          initialSchedules[item.pgdId] = eligible[0].scheduleId;
        }
      });
      setSelectedSchedules(initialSchedules);
      runFeasibility(initialSchedules);
      setStep(3);
      return;
    }
    if (step === 3) {
      const result = evaluateCoAdminMatrix(
        basket.map((item) => item.pgdId),
        tier1Data.departureDate,
      );
      setCoAdminResult(result);
      setCoAdminAckChecked(false);
      setStep(4);
      return;
    }
    if (step === 4) {
      if (!isStep3ConfirmEnabled()) return;
      buildPrescreenItems();
      setStep(5);
      return;
    }
  };

  const handleBack = () => {
    setStep((prev) => Math.max(1, prev - 1) as 1 | 2 | 3 | 4 | 5 | 6);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Step {step} of 6</p>
          <h2 className="text-lg font-semibold text-gray-900">
            {STEP_TITLES[step]}
          </h2>
        </div>
      </div>

      {step === 1 && (
        <div className="space-y-6">
          <div className="space-y-2 border rounded-lg p-4">
            <h3 className="font-medium text-gray-900">Trip summary</h3>
            <p className="text-sm text-gray-700">
              <span className="font-medium">Destinations: </span>
              {tier1Data.tripDestinations.join(", ")}
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-medium">Trip types: </span>
              {tier1Data.tripTypes
                .map((tripType) => TRIP_TYPE_LABELS[tripType])
                .join(", ")}
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-medium">Departure date: </span>
              {formatDateDDMMYYYY(tier1Data.departureDate)}
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-medium">Return date: </span>
              {formatDateDDMMYYYY(tier1Data.returnDate)}
            </p>
            {leadTimeDays !== null && (
              <p className="text-sm text-gray-700">
                <span className="font-medium">Days until departure: </span>
                {leadTimeDays}
              </p>
            )}
          </div>

          {tier1Data.priorVaccines.length > 0 && (
            <div className="space-y-2 border rounded-lg p-4">
              <h3 className="font-medium text-gray-900">
                Previous vaccinations reported
              </h3>
              <p className="text-xs text-gray-500">
                Recorded during health assessment — for reference when
                consulting NaTHNaC.
              </p>
              <ul className="space-y-2 mt-2">
                {tier1Data.priorVaccines.map((vaccine) => (
                  <li
                    key={vaccine.id}
                    className="text-sm text-gray-700 flex flex-wrap gap-x-3
                       gap-y-0.5"
                  >
                    <span className="font-medium">{vaccine.productName}</span>
                    {vaccine.approximateDate && (
                      <span className="text-gray-500">
                        {vaccine.approximateDate}
                      </span>
                    )}
                    {vaccine.doseNumber && (
                      <span className="text-gray-500">
                        Dose: {vaccine.doseNumber}
                      </span>
                    )}
                    {vaccine.givenElsewhere && (
                      <span className="text-gray-500">Given elsewhere</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-start space-x-2">
            <Checkbox
              id="nathnac-consulted"
              checked={nathnacConsulted}
              onCheckedChange={(checked) => {
                setNathnacConsulted(checked === true);
                setNathnacConsultedTouched(true);
              }}
            />
            <Label htmlFor="nathnac-consulted" className="font-normal">
              NaTHNaC (TravelHealthPro) has been consulted for the
              destination(s) listed above.
            </Label>
          </div>
          {nathnacConsultedTouched && !nathnacConsulted && (
            <p className="text-sm text-red-600 animate-in fade-in slide-in-from-top-1 duration-200">
              Please confirm NaTHNaC has been consulted.
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="nathnac-destinations">
              Destinations assessed on NaTHNaC
            </Label>
            <Textarea
              id="nathnac-destinations"
              rows={2}
              value={nathnacDestinationsAssessed}
              onChange={(e) => setNathnacDestinationsAssessed(e.target.value)}
              onBlur={() => setNathnacDestinationsAssessedTouched(true)}
            />
            {nathnacDestinationsAssessedTouched &&
              !nathnacDestinationsAssessed.trim() && (
                <p className="text-sm text-red-600 animate-in fade-in slide-in-from-top-1 duration-200">
                  Please enter the destinations assessed on NaTHNaC.
                </p>
              )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="nathnac-vaccines">
              Vaccines recommended / required
            </Label>
            <Textarea
              id="nathnac-vaccines"
              rows={2}
              value={nathnacVaccinesRecommended}
              onChange={(e) => setNathnacVaccinesRecommended(e.target.value)}
              onBlur={() => setNathnacVaccinesRecommendedTouched(true)}
            />
            {nathnacVaccinesRecommendedTouched &&
              !nathnacVaccinesRecommended.trim() && (
                <p className="text-sm text-red-600 animate-in fade-in slide-in-from-top-1 duration-200">
                  Please enter the vaccines recommended or required.
                </p>
              )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="nathnac-malaria">
              Malaria: risk, resistance &amp; chosen agent
            </Label>
            <Textarea
              id="nathnac-malaria"
              rows={2}
              value={nathnacMalariaRiskResistanceAgent}
              onChange={(e) =>
                setNathnacMalariaRiskResistanceAgent(e.target.value)
              }
              onBlur={() => setNathnacMalariaRiskResistanceAgentTouched(true)}
            />
            {nathnacMalariaRiskResistanceAgentTouched &&
              !nathnacMalariaRiskResistanceAgent.trim() && (
                <p className="text-sm text-red-600 animate-in fade-in slide-in-from-top-1 duration-200">
                  Please enter the malaria risk, resistance and chosen agent.
                </p>
              )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="nathnac-entry">
              Entry requirements (YF ICVP · MenACWY Hajj)
            </Label>
            <Textarea
              id="nathnac-entry"
              rows={2}
              value={nathnacEntryRequirements}
              onChange={(e) => setNathnacEntryRequirements(e.target.value)}
              onBlur={() => setNathnacEntryRequirementsTouched(true)}
            />
            {nathnacEntryRequirementsTouched &&
              !nathnacEntryRequirements.trim() && (
                <p className="text-sm text-red-600 animate-in fade-in slide-in-from-top-1 duration-200">
                  Please enter the entry requirements.
                </p>
              )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="nathnac-other">
              Other travel medicines indicated
            </Label>
            <Textarea
              id="nathnac-other"
              rows={2}
              value={nathnacOtherMedicinesIndicated}
              onChange={(e) =>
                setNathnacOtherMedicinesIndicated(e.target.value)
              }
              onBlur={() => setNathnacOtherMedicinesIndicatedTouched(true)}
            />
            {nathnacOtherMedicinesIndicatedTouched &&
              !nathnacOtherMedicinesIndicated.trim() && (
                <p className="text-sm text-red-600 animate-in fade-in slide-in-from-top-1 duration-200">
                  Please enter other travel medicines indicated (or "None").
                </p>
              )}
          </div>

          <div className="space-y-3">
            <h3 className="font-medium text-gray-900">Safety flags</h3>
            {triggeredFlags.length === 0 ? (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  No pre-selection safety flags triggered.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-3">
                {triggeredFlags.map((flagKey) => {
                  const meta = SAFETY_FLAG_META[flagKey];
                  if (!meta) return null;
                  const acked = acknowledgedFlags[flagKey] !== undefined;
                  return (
                    <div
                      key={flagKey}
                      className="border rounded-lg p-4 space-y-2"
                    >
                      <p className="font-medium text-gray-900">{meta.title}</p>
                      <p className="text-sm text-gray-700">{meta.text}</p>
                      <div className="flex items-start space-x-2 pt-1">
                        <Checkbox
                          id={`flag-ack-${flagKey}`}
                          checked={acked}
                          onCheckedChange={(checked) => {
                            handleFlagAckChange(flagKey, checked === true);
                            setSafetyFlagsTouched(true);
                          }}
                        />
                        <Label
                          htmlFor={`flag-ack-${flagKey}`}
                          className="font-normal"
                        >
                          I have reviewed this advisory and will apply it to
                          product selection.
                        </Label>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {safetyFlagsTouched &&
              triggeredFlags.some(
                (flagKey) => acknowledgedFlags[flagKey] === undefined,
              ) && (
                <p className="text-sm text-red-600 animate-in fade-in slide-in-from-top-1 duration-200">
                  Please acknowledge all safety flags above.
                </p>
              )}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6">
          {PRODUCT_GROUPS.map((group) => (
            <div key={group.label} className="space-y-3">
              <h3 className="font-medium text-gray-900">{group.label}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {group.pgdIds.map((pgdId) => renderProductTile(pgdId))}
              </div>
            </div>
          ))}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          {basket.length === 0 ? (
            <p className="text-sm text-gray-500">No products selected.</p>
          ) : (
            basket.map((item) => {
              const eligible = getEligibleSchedules(
                item.pgdId,
                patientAge,
                "",
              );
              const currentScheduleId = selectedSchedules[item.pgdId];
              const feas = feasibility.find((f) => f.pgdId === item.pgdId);

              return (
                <div
                  key={item.pgdId}
                  className="rounded-lg border border-gray-200 bg-white p-4 space-y-3"
                >
                  <p className="text-sm font-semibold text-gray-900">
                    {item.displayName}
                    {item.blackTriangle && (
                      <span className="ml-1 text-gray-500">▼</span>
                    )}
                  </p>

                  {eligible.length === 0 ? (
                    <p className="text-xs text-red-600">
                      No eligible schedule found for this patient.
                    </p>
                  ) : eligible.length === 1 ? (
                    <p className="text-xs text-gray-600">
                      Schedule: {eligible[0].label}
                    </p>
                  ) : (
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">
                        Schedule
                      </label>
                      <select
                        className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 bg-white"
                        value={currentScheduleId ?? ""}
                        onChange={(e) => {
                          const updated = {
                            ...selectedSchedules,
                            [item.pgdId]: e.target.value,
                          };
                          setSelectedSchedules(updated);
                          runFeasibility(updated);
                        }}
                      >
                        {eligible.map((s) => (
                          <option key={s.scheduleId} value={s.scheduleId}>
                            {s.label}
                            {s.offLabel ? " (off-label)" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {ANTIMALARIAL_PGDS.has(item.pgdId) ? (
                    (() => {
                      const brandId = getAntimalarialBrandIdForWeight(
                        item.pgdId,
                        tier1Data.weightKg,
                      );
                      const calc = calcAntimalarialSupply(
                        item.pgdId,
                        brandId,
                        tier1Data.departureDate,
                        tier1Data.returnDate,
                      );
                      if (!calc) return null;
                      return (
                        <div className="rounded-md bg-blue-50 border border-blue-200 p-3 space-y-1">
                          <p className="text-xs font-semibold text-blue-800">
                            Supply calculation
                          </p>
                          <p className="text-xs text-blue-800">
                            <span className="font-medium">Start: </span>
                            {calc.startDate} ({calc.startNote})
                          </p>
                          <p className="text-xs text-blue-800">
                            <span className="font-medium">Dose: </span>
                            {calc.doseNote}
                          </p>
                          <p className="text-xs text-blue-800">
                            {calc.afterNote}
                          </p>
                          <p className="text-xs font-semibold text-blue-900 pt-1">
                            Total supply: {calc.totalQtyLabel}
                          </p>
                        </div>
                      );
                    })()
                  ) : (
                    feas && (
                      <div className="space-y-2">
                        {feas.exceedsDeparture ? (
                          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 space-y-2">
                            <p className="text-xs font-medium text-amber-800">
                              ⚠ Course ends {feas.daysAfterDeparture} day
                              {feas.daysAfterDeparture !== 1 ? "s" : ""} after
                              departure (
                              {new Date(feas.lastDoseDate).toLocaleDateString(
                                "en-GB",
                              )}
                              )
                            </p>
                            {feas.hasAcceleratedAlternative &&
                              feas.acceleratedFitsDeparture &&
                              feas.acceleratedScheduleId && (
                                <div className="space-y-1">
                                  <p className="text-xs text-amber-700">
                                    Accelerated schedule available — completes{" "}
                                    {new Date(
                                      feas.acceleratedLastDoseDate!,
                                    ).toLocaleDateString("en-GB")}{" "}
                                    ✓
                                  </p>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    className="text-xs h-7 px-3 border-amber-400 text-amber-800 hover:bg-amber-100"
                                    onClick={() => {
                                      const updated = {
                                        ...selectedSchedules,
                                        [item.pgdId]: feas.acceleratedScheduleId!,
                                      };
                                      setSelectedSchedules(updated);
                                      runFeasibility(updated);
                                    }}
                                  >
                                    Switch to accelerated
                                  </Button>
                                </div>
                              )}
                            {feas.hasAcceleratedAlternative &&
                              !feas.acceleratedFitsDeparture && (
                                <p className="text-xs text-amber-700">
                                  No schedule completes before departure —
                                  pharmacist to advise.
                                </p>
                              )}
                          </div>
                        ) : (
                          <p className="text-xs text-green-700 font-medium">
                            ✓ Course completes before departure (
                            {new Date(feas.lastDoseDate).toLocaleDateString(
                              "en-GB",
                            )}
                            )
                          </p>
                        )}
                      </div>
                    )
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {step === 4 && coAdminResult && (
        <div className="space-y-6">
          <div className="space-y-3">
            <h3 className="font-medium text-gray-900">
              Co-administration and scheduling review
            </h3>
            {coAdminResult.constraints.length === 0 ? (
              <p className="text-sm text-gray-700">
                No co-administration constraints identified for the selected
                basket.
              </p>
            ) : (
              <div className="space-y-3">
                {coAdminResult.constraints.map((constraint, index) => {
                  const ruleType = findRuleType(constraint);
                  return (
                    <div
                      key={`${constraint.pgdIdA}-${constraint.pgdIdB}-${index}`}
                      className="border rounded-lg p-4 space-y-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium text-gray-900">
                          {constraint.productNameA} + {constraint.productNameB}
                        </p>
                        {ruleType && (
                          <Badge variant="outline">
                            {RULE_TYPE_LABELS[ruleType]}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-700">
                        {constraint.rule}
                      </p>
                      {constraint.schedulingRecommendation && (
                        <div>
                          <p className="text-xs font-medium text-gray-500">
                            Scheduling recommendation
                          </p>
                          <p className="text-sm text-gray-700">
                            {constraint.schedulingRecommendation}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="coadmin-ack"
                    checked={coAdminAckChecked}
                    onCheckedChange={(checked) =>
                      setCoAdminAckChecked(checked === true)
                    }
                  />
                  <Label htmlFor="coadmin-ack" className="font-normal">
                    I have reviewed all co-administration constraints and
                    scheduling recommendations above.
                  </Label>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <h3 className="font-medium text-gray-900">Basket summary</h3>
            <ul className="space-y-1">
              {basket.map((item) => (
                <li
                  key={item.pgdId}
                  className="text-sm text-gray-700 border rounded-md px-3 py-2"
                >
                  {item.displayName}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {step === 5 && (
        <div className="space-y-6">
          {prescreenRemovedProducts.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-900">
                Removed during screening
              </p>
              {prescreenRemovedProducts.map((r, i) => (
                <div
                  key={i}
                  className="border border-red-200 rounded-lg p-3 bg-red-50 space-y-1"
                >
                  <p className="text-sm font-semibold text-red-800">
                    {r.productName}
                  </p>
                  <p className="text-sm text-red-700">{r.reason}</p>
                </div>
              ))}
            </div>
          )}

          {!prescreenComplete && (
            <>
              {prescreenGates.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Pre-supply confirmations
                  </h3>
                  <div className="space-y-2">
                    {prescreenGates.map((gate) => (
                      <div
                        key={gate.key}
                        className="flex items-start gap-3 border rounded-lg px-4 py-3"
                      >
                        <Checkbox
                          id={`gate-${gate.key}`}
                          checked={gate.confirmed}
                          onCheckedChange={(checked) =>
                            setPrescreenGates((prev) =>
                              prev.map((g) =>
                                g.key === gate.key
                                  ? { ...g, confirmed: checked === true }
                                  : g,
                              ),
                            )
                          }
                        />
                        <div className="space-y-1">
                          <Label
                            htmlFor={`gate-${gate.key}`}
                            className="font-normal text-sm"
                          >
                            {gate.text}
                          </Label>
                          {gate.productNames.length > 1 && (
                            <p className="text-xs text-gray-500">
                              Applies to: {gate.productNames.join(", ")}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {prescreenExclusions.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Exclusion screening
                  </h3>
                  <div className="divide-y border rounded-lg overflow-hidden">
                    {prescreenExclusions.map((excl) => (
                      <div key={excl.key} className="px-4 py-3 space-y-2">
                        <div className="flex items-start justify-between gap-4">
                          <div className="space-y-1 flex-1">
                            <p className="text-sm text-gray-900">
                              {excl.question}
                            </p>
                            {excl.products.length > 1 && (
                              <p className="text-xs text-gray-500">
                                Applies to:{" "}
                                {[
                                  ...new Set(
                                    excl.products.map((p) => p.productName),
                                  ),
                                ].join(", ")}
                              </p>
                            )}
                            {excl.autoAnswered && excl.autoAnswerSource && (
                              <p className="text-xs text-blue-600">
                                Auto-filled: {excl.autoAnswerSource}
                              </p>
                            )}
                            {excl.explainerText && (
                              <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1 mt-1">
                                {excl.explainerText}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <Button
                              type="button"
                              size="sm"
                              variant={
                                excl.answer === "yes" ? "default" : "outline"
                              }
                              onClick={() =>
                                setPrescreenExclusions((prev) =>
                                  prev.map((e) =>
                                    e.key === excl.key
                                      ? {
                                          ...e,
                                          answer: "yes",
                                          autoAnswered: false,
                                        }
                                      : e,
                                  ),
                                )
                              }
                            >
                              Yes
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant={
                                excl.answer === "no" ? "default" : "outline"
                              }
                              onClick={() =>
                                setPrescreenExclusions((prev) =>
                                  prev.map((e) =>
                                    e.key === excl.key
                                      ? {
                                          ...e,
                                          answer: "no",
                                          autoAnswered: false,
                                        }
                                      : e,
                                  ),
                                )
                              }
                            >
                              No
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {prescreenCautions.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Caution review
                  </h3>
                  <div className="divide-y border rounded-lg overflow-hidden">
                    {prescreenCautions.map((caution) => (
                      <div key={caution.key} className="px-4 py-3 space-y-2">
                        <div className="flex items-start justify-between gap-4">
                          <div className="space-y-1 flex-1">
                            <p className="text-sm text-gray-900">
                              {caution.question}
                            </p>
                            {caution.products.length > 1 && (
                              <p className="text-xs text-gray-500">
                                Applies to:{" "}
                                {[
                                  ...new Set(
                                    caution.products.map(
                                      (p) => p.productName,
                                    ),
                                  ),
                                ].join(", ")}
                              </p>
                            )}
                            {caution.autoAnswered &&
                              caution.autoAnswerSource && (
                                <p className="text-xs text-blue-600">
                                  Auto-filled: {caution.autoAnswerSource}
                                </p>
                              )}
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <Button
                              type="button"
                              size="sm"
                              variant={
                                caution.answer === "yes"
                                  ? "default"
                                  : "outline"
                              }
                              onClick={() =>
                                setPrescreenCautions((prev) =>
                                  prev.map((c) =>
                                    c.key === caution.key
                                      ? {
                                          ...c,
                                          answer: "yes",
                                          autoAnswered: false,
                                        }
                                      : c,
                                  ),
                                )
                              }
                            >
                              Yes
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant={
                                caution.answer === "no"
                                  ? "default"
                                  : "outline"
                              }
                              onClick={() =>
                                setPrescreenCautions((prev) =>
                                  prev.map((c) =>
                                    c.key === caution.key
                                      ? {
                                          ...c,
                                          answer: "no",
                                          autoAnswered: false,
                                        }
                                      : c,
                                  ),
                                )
                              }
                            >
                              No
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {prescreenCautions.some((c) => c.answer === "yes") && (
                    <div className="space-y-3">
                      {prescreenCautions
                        .filter((c) => c.answer === "yes")
                        .flatMap((c) => c.products)
                        .map((p, i) => (
                          <div
                            key={`${p.checkId}-${i}`}
                            className="border rounded-md p-3"
                          >
                            <p className="text-xs font-medium text-gray-500 mb-1">
                              {p.productName}
                            </p>
                            <p className="text-sm text-gray-700">
                              {p.cautionMessage}
                            </p>
                          </div>
                        ))}
                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="prescreen-caution-ack"
                          checked={prescreenCautionAcknowledged}
                          onCheckedChange={(checked) =>
                            setPrescreenCautionAcknowledged(checked === true)
                          }
                        />
                        <Label
                          htmlFor="prescreen-caution-ack"
                          className="font-normal text-sm"
                        >
                          I have reviewed all cautions above and will apply
                          them to my clinical decision.
                        </Label>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <Button
                onClick={handleConfirmPrescreen}
                disabled={
                  prescreenGates.some((g) => !g.confirmed) ||
                  prescreenExclusions.some((e) => e.answer === undefined) ||
                  prescreenCautions.some((c) => c.answer === undefined) ||
                  (prescreenCautions.some((c) => c.answer === "yes") &&
                    !prescreenCautionAcknowledged)
                }
                className="w-full bg-slate-900 hover:bg-slate-800"
              >
                Confirm pre-screening
              </Button>
            </>
          )}

          {prescreenComplete && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-900">
                Pre-screening complete.
                {basket.length > 0
                  ? ` ${basket.length} product${basket.length === 1 ? "" : "s"} passed screening.`
                  : " No products remain in the basket."}
              </p>
              {basket.length === 0 && (
                <p className="text-sm text-gray-600">
                  No products remain. Return to the basket to make a new
                  selection.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {step === 6 && (
        <div className="space-y-6">
          {noFitWarnings.length > 0 && (
            <div className="border border-amber-200 rounded-lg p-4 bg-amber-50 space-y-2">
              <p className="text-sm font-semibold text-amber-800">
                The following products cannot complete before departure:
              </p>
              {noFitWarnings.map((pgdId) => {
                const warnedProduct = basket.find((b) => b.pgdId === pgdId);
                return (
                  <p key={pgdId} className="text-sm text-amber-700">
                    {warnedProduct?.displayName ?? pgdId}
                  </p>
                );
              })}
              <p className="text-sm text-amber-700">
                These are included in the plan. Some doses may need to be
                given abroad or deferred.
              </p>
            </div>
          )}
          {projectedSlots.length === 0 ? (
            <p className="text-sm text-gray-600">
              No appointments to schedule — all selected products are for
              supply only and have no dose intervals.
            </p>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                {projectedSlots.length} appointment
                {projectedSlots.length === 1 ? "" : "s"} projected. Dates are
                based on site availability and product schedules.
              </p>

              {projectedSlots.map((slot, index) => (
                <div
                  key={`${slot.date}-${index}`}
                  className="border rounded-lg p-4 space-y-3"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      {editingSlotIndex === index ? (
                        <div className="space-y-2">
                          <input
                            type="date"
                            className="border rounded px-2 py-1 text-sm"
                            value={slot.date}
                            min={new Date().toISOString().split("T")[0]}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (!val) return;
                              if (
                                isDateWithinTravelPeriod(
                                  val,
                                  tier1Data.departureDate,
                                  tier1Data.returnDate,
                                )
                              ) {
                                setSlotDateEditError(
                                  "Cannot select a date between departure and return — the patient will be traveling.",
                                );
                                return;
                              }
                              setSlotDateEditError("");
                              handleSlotDateChange(index, val);
                            }}
                            autoFocus
                          />
                          {slotDateEditError && (
                            <p className="text-xs text-red-600">
                              {slotDateEditError}
                            </p>
                          )}
                          {siteOpenDays && (
                            <p className="text-xs text-gray-500">
                              Site open: {siteOpenDays}
                            </p>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSlotDateEditError("");
                              setEditingSlotIndex(null);
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900">
                            {slot.dayOfWeek}{" "}
                            {new Date(
                              slot.date + "T00:00:00",
                            ).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })}
                          </p>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-xs text-gray-500 h-auto py-0 px-1"
                            onClick={() => {
                              setSlotDateEditError("");
                              setEditingSlotIndex(index);
                            }}
                          >
                            Edit date
                          </Button>
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 shrink-0">
                      Appointment {index + 1}
                    </span>
                  </div>

                  {pickingTimeForSlotIndex === index ? (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
                        <p className="text-xs font-medium text-gray-700">
                          Select a time for Appointment {index + 1}
                        </p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-xs h-auto py-0"
                          onClick={() => setPickingTimeForSlotIndex(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                      <AppointmentSlotPicker
                        isDarkMode={false}
                        initialDate={new Date(slot.date + "T00:00:00")}
                        travelDepartureDate={tier1Data.departureDate}
                        travelReturnDate={tier1Data.returnDate}
                        onSlotSelected={(selected) =>
                          handleSlotTimeSelected(index, selected)
                        }
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      {slot.selectedTime ? (
                        <>
                          <span className="text-sm text-gray-900 font-medium">
                            {slot.selectedTime}
                          </span>
                          {slot.selectedPharmacistName && (
                            <span className="text-xs text-gray-500">
                              {slot.selectedPharmacistName}
                            </span>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-xs h-auto py-0 px-1 text-gray-500"
                            onClick={() => setPickingTimeForSlotIndex(index)}
                          >
                            Change
                          </Button>
                        </>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => setPickingTimeForSlotIndex(index)}
                        >
                          Pick time
                        </Button>
                      )}
                    </div>
                  )}

                  <ul className="space-y-1">
                    {slot.doses.map((dose, doseIndex) => (
                      <li
                        key={`${dose.pgdId}-${dose.doseNumber}-${doseIndex}`}
                        className="flex items-start gap-2"
                      >
                        <span className="text-xs text-gray-400 mt-0.5">
                          •
                        </span>
                        <div>
                          <span className="text-sm text-gray-900">
                            {dose.productName}
                            {dose.blackTriangle ? " ▼" : ""}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">
                            {dose.doseLabel}
                          </span>
                          {dose.isAfterDeparture && (
                            <span className="text-xs text-red-600 ml-2">
                              After departure
                            </span>
                          )}
                          {dose.wasAdjusted && (
                            <span className="text-xs text-amber-600 ml-2">
                              Date adjusted for availability
                            </span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {basket.some((item) => ANTIMALARIAL_PGDS.has(item.pgdId)) && (
            <div className="space-y-3">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold text-gray-900">
                  Antimalarial supply
                </h3>
                <p className="text-xs text-gray-500">
                  Antimalarials are supplied separately. Set a supply date
                  when ready — this can be left blank and updated later.
                </p>
              </div>

              {basket
                .filter((item) => ANTIMALARIAL_PGDS.has(item.pgdId))
                .map((item) => {
                  const brandId = selectedSchedules[item.pgdId] ?? "";
                  const calc = calcAntimalarialSupply(
                    item.pgdId,
                    brandId,
                    tier1Data.departureDate,
                    tier1Data.returnDate,
                  );
                  const supplyDate = supplyTaskDates[item.pgdId] ?? "";

                  return (
                    <div
                      key={item.pgdId}
                      className="border rounded-lg p-4 space-y-3"
                    >
                      <p className="text-sm font-semibold text-gray-900">
                        {item.displayName}
                      </p>

                      {calc && (
                        <div className="space-y-1 text-xs text-gray-600">
                          <p>
                            <span className="font-medium">Total supply: </span>
                            {calc.totalQtyLabel}
                          </p>
                          <p>
                            <span className="font-medium">Dose: </span>
                            {calc.doseNote}
                          </p>
                          <p>{calc.startNote} ({calc.startDate})</p>
                          <p>{calc.afterNote}</p>
                        </div>
                      )}

                      <div className="space-y-1">
                        <label className="text-xs font-medium text-gray-700">
                          Date to supply (optional)
                        </label>
                        <input
                          type="date"
                          className="border rounded px-2 py-1 text-sm w-full"
                          value={supplyDate}
                          min={todayIsoDate()}
                          max={tier1Data.departureDate}
                          onChange={(e) =>
                            setSupplyTaskDates((prev) => ({
                              ...prev,
                              [item.pgdId]: e.target.value,
                            }))
                          }
                        />
                        {!supplyDate && (
                          <p className="text-xs text-gray-400">
                            No date set — can be added later.
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}

          {intervalViolations.length > 0 && (
            <div className="border border-red-300 bg-red-50 rounded-lg p-4 space-y-3">
              <p className="text-sm font-semibold text-red-800">
                Minimum interval not met - this dose will not count as given
              </p>
              {intervalViolations.map((v, i) => (
                <p key={i} className="text-sm text-red-700">
                  {v.productName} - {v.doseLabel}: scheduled {v.actualDate},
                  but the earliest valid date is {v.requiredDate}.
                </p>
              ))}
              <div className="space-y-2">
                <Label htmlFor="interval-override-reason">
                  Reason for proceeding despite this (required)
                </Label>
                <Textarea
                  id="interval-override-reason"
                  value={intervalOverrideReason}
                  onChange={(e) => setIntervalOverrideReason(e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          )}

          {(() => {
            const slotsWithoutDetails = projectedSlots.filter(
              (s) => !(s as AppointmentSlotWithTime).selectedTime,
            ).length;
            const hasIntervalBlock =
              intervalViolations.length > 0 && !intervalOverrideReason.trim();
            const isDisabled = slotsWithoutDetails > 0 || hasIntervalBlock;

            return (
              <>
                {slotsWithoutDetails > 0 && (
                  <p className="text-sm text-amber-700 text-center">
                    {slotsWithoutDetails === 1
                      ? "1 appointment still needs a time, service and pharmacist — use Pick Time above."
                      : `${slotsWithoutDetails} appointments still need a time, service and pharmacist — use Pick Time above.`}
                  </p>
                )}
                <Button
                  onClick={handleCreatePlan}
                  disabled={isDisabled}
                  className="w-full bg-slate-900 hover:bg-slate-800"
                >
                  Confirm and create Travel Health Plan
                </Button>
              </>
            );
          })()}
        </div>
      )}

      {step !== 6 && (
        <div className="flex justify-between pt-4 border-t border-gray-200">
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={step === 1 ? onCancel : handleBack}
            >
              {step === 1 ? "Cancel" : "Back"}
            </Button>
            {step > 1 && (
              <Button variant="ghost" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>

          {step < 5 ? (
            <Button
              onClick={handleNext}
              disabled={
                step === 1
                  ? !isStep1Valid()
                  : step === 2
                    ? !isStep2Valid()
                    : step === 4
                      ? !isStep3ConfirmEnabled()
                      : false
              }
              className="bg-slate-900 hover:bg-slate-800"
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleRunProjection}
              disabled={!isStep5Valid()}
              className="bg-slate-900 hover:bg-slate-800"
            >
              Review appointment schedule
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export type { TravelHealthTier1SelectionProps, BasketItem, DengueGateState };
